import { useState } from 'react'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Divider from '@mui/material/Divider'
import { useNavigate } from 'react-router-dom'
import type { CartItem, CartItemModifier } from '@/shared/types/cart'
import type { MenuItem, MenuItemDetail, MenuModifierSet } from '@/shared/api/menu'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { PATHS } from '@/router/paths'
import TicketItemModifier from '@/components/menu/order-details/TicketItemModifier'
import {
	ItemCard,
	ItemImage,
	ItemImagePlaceholder,
	ItemDetails,
	ItemNameGroup,
	ItemName,
	ItemModifiers,
	ItemPrice,
	ItemActions,
	ModifierToggleBtn,
	QtyExpand,
	QtyExpandShell,
	QtyExpandTrack,
	QtyExpandLead,
	QtyExpandBox,
	QtyExpandBtn,
	QtyExpandTrashOutside,
	QTY_EXPAND_SHELL_CLASS,
	QTY_EXPAND_TRACK_CLASS,
	QTY_EXPAND_BOX_CLASS,
	QTY_EXPAND_TRASH_OUTSIDE_CLASS,
} from './CartItemRow.styled'

// Ember parity (cart-item-modifier.js subtotal + cart-item.js modifierTotal):
// Recursively sums modifier prices across the full modifier tree.
// unitPrice = modifier.price (flat) or itemBasePrice * modifier.percent (percent type).
// percent > 0 indicates a percent-type modifier (Ember: modifier.type === 'percent').
function calcModifierTotal(
	modifiers: CartItemModifier[],
	modifierSets: MenuModifierSet[],
	itemBasePrice: number,
): number {
	let total = 0
	for (const cartMod of modifiers) {
		let found: { price: number; percent: number; modifierSets: MenuModifierSet[] } | null = null
		for (const set of modifierSets) {
			if (set.ref === cartMod.modifierSetRef) {
				const mod = set.modifiers.find((m) => m.ref === cartMod.modifierRef)
				if (mod) {
					found = mod
					break
				}
			}
			for (const mod of set.modifiers) {
				const deepSet = mod.modifierSets.find((s) => s.ref === cartMod.modifierSetRef)
				if (deepSet) {
					const deepMod = deepSet.modifiers.find((m) => m.ref === cartMod.modifierRef)
					if (deepMod) {
						found = deepMod
						break
					}
				}
			}
			if (found) break
		}
		if (found) {
			const unitPrice = found.percent > 0 ? itemBasePrice * found.percent : found.price
			total += unitPrice * cartMod.quantity
			total += calcModifierTotal(cartMod.modifiers, found.modifierSets, itemBasePrice)
		}
	}
	return total
}

export interface CartItemRowProps {
	item: CartItem
	menuItem: MenuItem | undefined
	menuItemDetail?: MenuItemDetail
	locationRef: string
	onRemove: (item: CartItem) => void
	onIncrement: (item: CartItem) => void
	onDecrement: (item: CartItem) => void
	currency?: string
	locale?: string
	showDivider?: boolean
}

const MODIFIER_COLLAPSE_THRESHOLD = 2

export function CartItemRow({
	item,
	menuItem,
	menuItemDetail,
	locationRef,
	onRemove,
	onIncrement,
	onDecrement,
	currency = 'USD',
	locale = 'en-US',
	showDivider = false,
}: CartItemRowProps) {
	const { t } = useFormatMessage()
	const navigate = useNavigate()
	const [showAllModifiers, setShowAllModifiers] = useState(false)

	// Ember parity (ticket-item.hbs line 3): renders blank when menu record is not loaded —
	// never surfaces a raw ref string as a display name.
	const name = menuItem?.name ?? ''
	// Ember parity (ticket-item.js itemTotal): display price = base item price + all modifier prices.
	// Modifier detail only available once menuItemDetail has loaded — falls back to base price until then.
	const price = menuItem
		? (() => {
				const basePrice = menuItem.onlinePrice ?? menuItem.price
				const modifierTotal =
					menuItemDetail && item.modifiers.length > 0
						? calcModifierTotal(item.modifiers, menuItemDetail.modifierSets, basePrice)
						: 0
				return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
					(basePrice + modifierTotal) / 100,
				)
			})()
		: null

	// Special request counts toward the overflow threshold (Figma: shown only in expanded state).
	const totalRows = item.modifiers.length + (item.specialRequest ? 1 : 0)
	const hasOverflow = totalRows > MODIFIER_COLLAPSE_THRESHOLD
	const visibleModifiers =
		hasOverflow && !showAllModifiers
			? item.modifiers.slice(0, MODIFIER_COLLAPSE_THRESHOLD)
			: item.modifiers
	const hiddenCount = totalRows - MODIFIER_COLLAPSE_THRESHOLD

	function handleCardClick() {
		void navigate(PATHS.editItem(locationRef, item.id))
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.target !== e.currentTarget) return
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			handleCardClick()
		}
	}

	function handleRemoveClick(e: React.MouseEvent) {
		e.stopPropagation()
		onRemove(item)
	}

	function handleDecrementClick(e: React.MouseEvent) {
		e.stopPropagation()
		if (item.quantity === 1) {
			onRemove(item)
		} else {
			onDecrement(item)
		}
	}

	function handleIncrementClick(e: React.MouseEvent) {
		e.stopPropagation()
		onIncrement(item)
	}

	function handleToggleModifiers(e: React.MouseEvent) {
		e.stopPropagation()
		setShowAllModifiers((prev) => !prev)
	}

	const leadAriaLabel =
		item.quantity === 1
			? t('component:cartItemRow:remove:label', { name })
			: t('component:cartItemRow:decrement:label', { name })

	return (
		<>
			<ItemCard
				role="button"
				tabIndex={0}
				aria-label={t('component:cartItemRow:edit:label', { name })}
				onClick={handleCardClick}
				onKeyDown={handleKeyDown}
				data-testid="cart-item-row"
			>
				{menuItem?.imageUrl800x800 ? (
					<ItemImage src={menuItem.imageUrl800x800} alt="" loading="lazy" />
				) : (
					<ItemImagePlaceholder aria-hidden="true" />
				)}

				<ItemDetails>
					<ItemNameGroup>
						<ItemName>{name}</ItemName>
						{item.modifiers.length > 0 &&
							menuItemDetail &&
							visibleModifiers.map((modifier) => (
								<TicketItemModifier
									key={modifier.modifierRef}
									modifier={modifier}
									menuItem={menuItemDetail}
									itemBasePrice={
										menuItemDetail.onlinePrice ?? menuItemDetail.price
									}
									locale={locale}
									currency={currency}
								/>
							))}
						{item.specialRequest && (!hasOverflow || showAllModifiers) && (
							<ItemModifiers>
								{t('component:cartItemRow:specialRequest:label', {
									text: item.specialRequest,
								})}
							</ItemModifiers>
						)}
						{hasOverflow &&
							(showAllModifiers ? (
								<ModifierToggleBtn onClick={handleToggleModifiers}>
									{t('component:cartItemRow:modifiers:showLess')}
								</ModifierToggleBtn>
							) : (
								<ModifierToggleBtn onClick={handleToggleModifiers}>
									{t('component:cartItemRow:modifiers:showMore', {
										count: hiddenCount,
									})}
								</ModifierToggleBtn>
							))}
					</ItemNameGroup>
					{price && <ItemPrice>{price}</ItemPrice>}
				</ItemDetails>

				<ItemActions>
					{/* CSS-driven expand — no JS hover state.
					    QtyExpand starts at 40px (count only), expands to 120px on :hover/:focus-within.
					    The track slides left-to-right revealing ± buttons (olo-demo parity). */}
					<QtyExpand role="group" aria-label={`${name} quantity`}>
						{/* Trash icon shown outside the pill at collapsed size — hides on expand */}
						<QtyExpandTrashOutside
							className={QTY_EXPAND_TRASH_OUTSIDE_CLASS}
							aria-label={t('component:cartItemRow:remove:label', { name })}
							onClick={handleRemoveClick}
							data-testid="qty-trash-outside"
						>
							<DeleteOutlinedIcon sx={{ fontSize: 20 }} />
						</QtyExpandTrashOutside>

						<QtyExpandShell className={QTY_EXPAND_SHELL_CLASS}>
							<QtyExpandTrack className={QTY_EXPAND_TRACK_CLASS}>
								{/* Lead button: trash (qty=1) or minus (qty>1) */}
								<QtyExpandLead>
									<QtyExpandBtn
										aria-label={leadAriaLabel}
										onClick={handleDecrementClick}
										data-testid="qty-lead-btn"
									>
										{item.quantity === 1 ? (
											<DeleteOutlinedIcon sx={{ fontSize: 20 }} />
										) : (
											<RemoveIcon sx={{ fontSize: 20 }} />
										)}
									</QtyExpandBtn>
								</QtyExpandLead>

								<QtyExpandBox
									className={QTY_EXPAND_BOX_CLASS}
									aria-label={`Quantity: ${item.quantity}`}
								>
									{item.quantity}
								</QtyExpandBox>

								<QtyExpandBtn
									aria-label={t('component:cartItemRow:increment:label', {
										name,
									})}
									onClick={handleIncrementClick}
									data-testid="qty-plus-btn"
								>
									<AddIcon sx={{ fontSize: 20 }} />
								</QtyExpandBtn>
							</QtyExpandTrack>
						</QtyExpandShell>
					</QtyExpand>
				</ItemActions>
			</ItemCard>
			{showDivider && <Divider />}
		</>
	)
}
